import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { promotionTypesAPI, languageSupportAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManagePromotions, canDeletePromotions } from '../../utils/roleUtils';

const defaultTranslation = () => ({ languageCode: 'en', name: '' });

const normalizeList = (response) => {
  if (!response?.data) return [];
  const d = response.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.payload)) return d.payload;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const normalizeSingle = (response) => {
  if (!response?.data) return null;
  const d = response.data;
  if (d?.payload && typeof d.payload === 'object') return d.payload;
  if (d?.data && typeof d.data === 'object') return d.data;
  if (typeof d === 'object' && !Array.isArray(d)) return d;
  return null;
};

const PromotionType = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canAddEdit = canManagePromotions(adminUser?.role);
  const canDelete = canDeletePromotions(adminUser?.role);

  const [list, setList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    translations: [defaultTranslation()],
  });
  const [languages, setLanguages] = useState([]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchLanguages = async () => {
    try {
      const response = await languageSupportAPI.getLanguages(0, 500);
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const activeLanguages = (response.data.payload || []).filter((l) => l.isActive !== false);
        setLanguages(activeLanguages);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    }
  };

  const fetchPromotionTypes = useCallback(
    async (opts = {}) => {
      const isCancelled = typeof opts.isCancelled === 'function' ? opts.isCancelled : () => false;
      setIsLoading(true);
      try {
        const response = await promotionTypesAPI.getPromotionTypes();
        if (isCancelled()) return;
        const payload = normalizeList(response);
        const apiTotal = response?.data?.totalRecord;
        const total = apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : payload.length;
        setList(total === 0 ? [] : payload.slice(0, total));
        setTotalRecord(total);
      } catch (error) {
        if (isCancelled()) return;
        console.error('Error fetching promotion types:', error);
        setList([]);
        setTotalRecord(0);
      } finally {
        if (!isCancelled()) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    fetchPromotionTypes({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [fetchPromotionTypes]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const mapToForm = (item) => ({
    id: item?.id ?? null,
    name: item?.name || '',
    translations: (item?.translations && item.translations.length)
      ? item.translations.map((t) => ({ languageCode: t.languageCode || '', name: t.name || '' }))
      : [defaultTranslation()],
  });

  const handleOpenAdd = () => {
    setFormData({
      id: null,
      name: '',
      translations: [defaultTranslation()],
    });
    setEditMode(false);
    setOpen(true);
  };

  const handleOpenEdit = async (item) => {
    setIsLoading(true);
    try {
      const response = await promotionTypesAPI.getPromotionType(item.id);
      const single = normalizeSingle(response);
      setFormData(mapToForm(single || item));
      setEditMode(true);
      setOpen(true);
    } catch (error) {
      console.error('Error fetching promotion type by id:', error);
      setFormData(mapToForm(item));
      setEditMode(true);
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleTranslationChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.translations];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, translations: next };
    });
  };

  const addTranslation = () => {
    setFormData((prev) => ({
      ...prev,
      translations: [...prev.translations, defaultTranslation()],
    }));
  };

  const removeTranslation = (index) => {
    if (formData.translations.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    if (!formData.name || !formData.name.trim()) {
      showToast(t('accTypes.toastNameRequired'), 'warning');
      return false;
    }
    for (let i = 0; i < formData.translations.length; i++) {
      const row = formData.translations[i];
      if (!row.languageCode || !row.languageCode.trim()) {
        showToast(t('accTypes.translationLangRequired').replace('{n}', String(i + 1)), 'warning');
        return false;
      }
      if (!row.name || !row.name.trim()) {
        showToast(t('accTypes.translationNameRequired').replace('{n}', String(i + 1)), 'warning');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      id: editMode ? Number(formData.id || 0) : 0,
      name: formData.name.trim(),
      translations: formData.translations.map((t) => ({
        languageCode: t.languageCode.trim(),
        name: t.name.trim(),
      })),
    };

    setIsSubmitting(true);
    try {
      if (editMode) {
        await promotionTypesAPI.updatePromotionType(payload);
        showToast('Promotion type updated successfully.', 'success');
      } else {
        await promotionTypesAPI.createPromotionType(payload);
        showToast('Promotion type added successfully.', 'success');
      }
      await fetchPromotionTypes();
      handleClose();
    } catch (error) {
      console.error('Error saving promotion type:', error);
      showToast(error?.response?.data?.errorMessage || 'Error saving. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('promoTypes.confirmDelete'))) return;
    try {
      await promotionTypesAPI.deletePromotionType(id);
      await fetchPromotionTypes();
      showToast(t('promoTypes.deleted'), 'success');
    } catch (error) {
      console.error('Error deleting promotion type:', error);
      showToast(error?.response?.data?.errorMessage || t('accTypes.deleteError'), 'error');
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paginatedList = list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
      >
        <Typography variant="h4" color="primary">
          Promotion Type
        </Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAdd}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Add Promotion Type
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
          borderRadius: 2,
          '& .MuiTableCell-head': {
            backgroundColor: '#ebf4f4',
            color: '#0F3D3E',
            fontWeight: 600,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#ebf4f4',
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.id')}</TableCell>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell>{t('common.translations')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  {t('promoTypes.empty')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedList.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {row.translations?.length
                      ? row.translations.map((t) => `${t.languageCode}: ${t.name}`).join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(row)}
                      sx={{ color: 'primary.main', mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRecord}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? t('promoTypes.dialogEdit') : t('promoTypes.dialogAdd')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('common.name')}
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('promoTypes.fieldNamePh')}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('common.translationsRequired')}
                </Typography>
                {formData.translations.map((tr, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <FormControl required size="small" sx={{ flex: 1 }}>
                      <InputLabel id={`promotion-lang-select-${index}`}>{t('common.language')}</InputLabel>
                      <Select
                        labelId={`promotion-lang-select-${index}`}
                        label={t('common.language')}
                        value={tr.languageCode || ''}
                        onChange={(e) => handleTranslationChange(index, 'languageCode', e.target.value)}
                      >
                        {languages.map((lang) => (
                          <MenuItem key={lang.id || lang.languageCode} value={lang.languageCode || ''}>
                            {lang.name || lang.languageCode || ''}
                          </MenuItem>
                        ))}
                        {tr.languageCode && !languages.some((l) => (l.languageCode || '') === tr.languageCode) && (
                          <MenuItem value={tr.languageCode}>{tr.languageCode}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      required
                      size="small"
                      label={t('common.name')}
                      value={tr.name}
                      onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                      placeholder={t('common.translatedNamePh')}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeTranslation(index)}
                      disabled={formData.translations.length <= 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={addTranslation}
                  sx={{ mt: 1 }}
                >
                  Add translation
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
            }}
          >
            {isSubmitting ? t('common.saving') : editMode ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromotionType;
